import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from 'react-redux';
import { ErrorMessage, useFormikContext } from 'formik';
import ReactQuill from "react-quill";
import * as XLSX from 'xlsx';
import StaticPopup from "components/StaticPopup";
import { UNITOPTIONS } from 'config'
import { debounce } from "lodash";


const downloatTemplate = "https://storagereponeevaydevcdn.blob.core.windows.net/business/material_BulkUpload_Template_n.xlsx"

const AddIndividual = ({ headerAction } = {}) => {
    const { values, setFieldValue } = useFormikContext();
    const [activeUnitIndex, setActiveUnitIndex] = useState(null);
    const [openUploadFile, setOpenUploadFile] = useState(false);
    const [fileError, setFileError] = useState('');
    const dropdownRef = useRef(null);
    const fileInputRef = useRef(null);


    const auth = useSelector((state) => state.auth);
    const user = auth?.user;
    // Transform entity units if available
    const entityUnits = user?.userEntity?.units || [];

    const dropdownOptions = useMemo(() => {
        if (entityUnits.length > 0) {
            return entityUnits.map(unit => `${unit.code} (${unit.name})`);
        }
        return UNITOPTIONS;
    }, [entityUnits]);

    const memoizedMaterials = useMemo(() => values.materials, [values.materials]);

    const handleBulkUpload = useCallback(() => {
        setOpenUploadFile(prev => !prev);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    const isMaterialEmpty = useCallback((material) => (
        !material?.name && !material?.quantity
    ), []);

    const processExcelFile = useCallback(async (file) => {
        const data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });

        const workbook = XLSX.read(data, { type: "array" });

        let unitMapping = {};

        // Only read instructions if custom headers are not present
        if (!user?.userEntity?.materialSheetHeaders?.length) {
            const instructionsSheet = workbook.Sheets["Instructions (Must Read)"];

            if (instructionsSheet) {
                const instructionsData = XLSX.utils.sheet_to_json(instructionsSheet, {
                    header: 1,
                    range: "A17:B26",
                });

                instructionsData.forEach((row) => {
                    if (row[0] && row[1]) {
                        unitMapping[row[1].toString().toLowerCase().trim()] = row[0];
                    }
                });
            }
        }

        const materialsSheet = workbook.Sheets["Material LIst"];
        if (!materialsSheet) throw new Error("Materials sheet missing");

        const materialsData = XLSX.utils.sheet_to_json(materialsSheet, { header: 1 });
        return { unitMapping, materialsData };
    }, []);

    const mapToMaterials = useCallback((sheetData, unitMapping, entityUnits, materialSheetHeaders = []) => {
        if (sheetData.length <= 1) {
            throw new Error("The uploaded file contains no data rows");
        }

        const materials = [];
        const errors = [];
        const entityUnitCodes = entityUnits.map(unit => unit.code.toUpperCase());

        const headerRow = sheetData[0];
        const dataRows = sheetData.slice(1);

        // Create a header index map for dynamic header mapping
        const headerIndexMap = {};
        const usingCustomHeaders = materialSheetHeaders.length > 0;

        if (usingCustomHeaders) {
            materialSheetHeaders.forEach((header, i) => {
                if (header && !headerIndexMap.hasOwnProperty(header.toLowerCase())) {
                    headerIndexMap[header.toLowerCase()] = i;
                }
            });
        }

        dataRows.forEach((row, idx) => {
            try {
                const isEmptyRow = !row.some(cell => cell !== null && cell !== undefined && cell !== '');
                if (isEmptyRow) return;

                const rowErrors = [];
                const rowNum = idx + 2;

                const getValue = (fieldName) => {
                    if (!usingCustomHeaders) return null;
                    const index = headerIndexMap[fieldName.toLowerCase()];
                    return index !== undefined ? row[index] : undefined;
                };

                let code = "";
                let name = "";
                let quantity = "";
                let rawUnit = "";
                let budget = "";
                let brandsRaw;
                let specifications = "";


                if (usingCustomHeaders) {
                    code = getValue("Item Code") ?? "";
                    name = getValue("Description") ?? "";
                    rawUnit = (getValue("Unit") ?? "")?.toString().toLowerCase().trim();
                    quantity = (getValue("Quantity") ?? "")?.toString();
                    budget = (getValue("Budget Rate") ?? "")?.toString();
                    brandsRaw = headerIndexMap.hasOwnProperty("make")
                        ? getValue("Make")
                        : undefined;
                    specifications = headerIndexMap.hasOwnProperty("long description")
                        ? getValue("Long Description")
                        : "";
                } else {
                    code = row[0]?.toString() ?? "";
                    name = row[1]?.toString() ?? "";
                    brandsRaw = row[2];
                    specifications = row[3]?.toString() ?? "";
                    quantity = row[4]?.toString() ?? "";
                    rawUnit = row[5]?.toString()?.toLowerCase().trim() ?? "";
                    budget = row[6]?.toString() ?? "";
                }
                // let budget;


                // Limit to 2 digits after decimal
                let formattedQuantity = quantity;
                if (quantity && !isNaN(quantity)) {
                    const match = quantity.toString().match(/^(\d+)(\.\d{0,2})?/);
                    formattedQuantity = match ? match[1] + (match[2] || "") : quantity;
                }



                if (!name) rowErrors.push("Material Name is required");
                if (!quantity) rowErrors.push("Quantity is required");

                if (!rawUnit) {
                    rowErrors.push("Unit is required");
                } else if (entityUnits.length > 0 && !entityUnitCodes.includes(rawUnit.toUpperCase())) {
                    rowErrors.push(`Invalid unit code "${rawUnit}" at row ${rowNum}`);
                }

                if (quantity && isNaN(parseFloat(quantity))) {
                    rowErrors.push("Quantity must be a number");
                }

                if (rowErrors.length > 0) throw new Error(`Row ${rowNum}: ${rowErrors.join(", ")}`);

                let finalUnit = rawUnit;
                if (entityUnits.length > 0) {
                    const matched = entityUnits.find(u => u.code.toLowerCase() === rawUnit);
                    finalUnit = matched ? `${matched.code} (${matched.name})` : rawUnit;
                } else {
                    finalUnit = unitMapping[rawUnit] || rawUnit;
                }

                materials.push({
                    code,
                    name,
                    brands: brandsRaw !== undefined && brandsRaw !== null
                        ? brandsRaw.toString().split(",").map(b => b.trim())
                        : [],
                    specifications,
                    quantity: formattedQuantity,
                    unit: finalUnit,
                    budget: budget ? Number(budget) : 0,
                });
            } catch (error) {
                errors.push(error.message);
            }
        });

        if (errors.length > 0) throw new Error(errors.join("\n"));
        if (materials.length === 0) throw new Error("No valid material data found");

        return materials;
    }, []);


    const handleFileUploadMaterial = useCallback(async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            if (!file.name.match(/\.(xlsx|xls)$/i)) {
                throw new Error("Please upload a valid Excel file (.xlsx or .xls)");
            }

            const { materialsData, unitMapping } = await processExcelFile(file);
            const uploadedMaterials = mapToMaterials(
                materialsData,
                unitMapping,
                entityUnits,
                user?.userEntity?.materialSheetHeaders || [] // Pass headers
            );

            const existingMaterials = values.materials.filter(m => !isMaterialEmpty(m));
            await setFieldValue("materials", [...existingMaterials, ...uploadedMaterials]);

            if (uploadedMaterials.length > 0) {
                setFileError('');
                setOpenUploadFile(false);
            }
        } catch (error) {
            setFileError(error.message);
            console.error("Upload error:", error);
        }
    }, [values.materials, isMaterialEmpty, processExcelFile, mapToMaterials, setFieldValue, entityUnits]);


    const handleQuillChange = useCallback(
        debounce((fieldPath, value) => {
            setFieldValue(fieldPath, value);
        }, 100),
        [setFieldValue]
    );

    useEffect(() => {
        return () => {
            handleQuillChange.cancel();
        };
    }, [handleQuillChange]);

    const removeRow = useCallback((index) => {
        if (values.materials.length > 1) {
            const updatedMaterials = values.materials.filter((_, i) => i !== index);
            setFieldValue('materials', updatedMaterials);
        }
    }, [values.materials, setFieldValue]);

    const addNewRow = useCallback(() => {
        const newMaterial = {
            code: "",
            name: "",
            quantity: "",
            unit: "",
            budget: 0,
            brands: [],
            specifications: "",
        };
        setFieldValue('materials', [...values.materials, newMaterial]);
    }, [values.materials, setFieldValue]);

    const downloadMaterialTemplate = () => {
        if (user?.userEntity?.materialSheetHeaders?.length) {
            const headers = user.userEntity.materialSheetHeaders;
            if (!headers.some(h => h.toLowerCase() === "budget rate")) {
                headers.splice(4, 0, "Budget Rate");  // Insert at column E
            }

            const worksheet = XLSX.utils.aoa_to_sheet([headers]);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Material LIst");

            // Generate Excel file as Blob
            const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
            const blob = new Blob([excelBuffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            // Create a download link and trigger it
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "Material_Upload_Template.xlsx";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url); // Clean up
        } else {
            // Fallback to static template
            window.open(downloatTemplate, "_blank");
        }
    };

    return (
        <>
            <div className="relative flex mb-2">
                {values?.requirementType === "contractor" ? (
                    <p className="inline-flex items-center">
                        Material in Scope
                        <span className="ml-2 relative group bg-gray-200 rounded-full flex items-center justify-center w-4 h-4 text-[10px] cursor-pointer group-hover:text-gray-600">
                            i
                            <div className="absolute bottom-4 mt-1 hidden w-48 shadow-md border p-2 text-xs text-black bg-white rounded group-hover:block">
                                If there are materials, materials need to be provided.
                            </div>
                        </span>
                    </p>
                ) : (
                    <div className="w-full justify-start items-center self-start text-sm font-medium gap-5">
                        Material List
                    </div>
                )}

                {headerAction && <div className="ml-auto mr-2">{headerAction}</div>}

                <button
                    type="button"
                    className="hover:border-black border-gray-300 border px-4 py-1 rounded-sm text-sm flex items-center justify-center text-left whitespace-nowrap"
                    onClick={handleBulkUpload}
                >
                    <img
                        src="https://storagereponeevaydevcdn.blob.core.windows.net/business/create_rfq_bulk_upload.svg"
                        alt=""
                        className="pr-2"
                    />
                    Bulk Upload
                </button>
            </div>

            <div className="flex items-center justify-center w-full">
                <div className="bg-gray-500 bg-opacity-50 w-full">
                    <div className="border border-gray-300 bg-white w-full">
                        <div className="bg-gray-100 grid grid-cols-22 gap-y-1 gap-x-2 text-sm text-[#777777] px-2 pt-3 pb-2">
                            <p className="text-center">Sr#</p>
                            <p className="text-center col-span-1">Code</p>
                            <p className="col-span-4">Material Name<span className="text-red-500 ml-0.5">*</span></p>
                            <p className="col-span-1">Qty<span className="text-red-500 ml-0.5">*</span></p>
                            <p className="col-span-2">Unit<span className="text-red-500 ml-0.5">*</span></p>
                            <p className="col-span-2">Budget Rate</p>
                            <p className="col-span-4">Material Brands <span className="text-[10px]">(comma separated)</span></p>
                            <p className="col-span-6 text-center">Specifications</p>
                            <p className="col-span-1"></p>
                        </div>

                        <div className="p-2 text-sm">
                            {memoizedMaterials.map((material, index) => (
                                <MaterialRow
                                    key={index}
                                    material={material}
                                    index={index}
                                    setFieldValue={setFieldValue}
                                    handleQuillChange={handleQuillChange}
                                    removeRow={removeRow}
                                    addNewRow={addNewRow}
                                    dropdownRef={dropdownRef}
                                    activeUnitIndex={activeUnitIndex}
                                    setActiveUnitIndex={setActiveUnitIndex}
                                    showRemove={values.materials.length > 1}
                                    dropdownOptions={dropdownOptions}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <StaticPopup
                openModal={openUploadFile}
                handleClose={() => {
                    setOpenUploadFile(false);
                    setFileError("");
                }}
                size="sm"
                width="xl"
                bodyStyle="pb-0 pt-0"
                className="px-0"
                header={<div className='text-base'>Upload Files</div>}
            >
                <UploadFileSection
                    handleFileUploadMaterial={handleFileUploadMaterial}
                    fileError={fileError}
                    downloadMaterialTemplate={downloadMaterialTemplate}
                    setOpenUploadFile={setOpenUploadFile}
                />
            </StaticPopup>
        </>
    );
};

const MaterialRow = React.memo(({
    material,
    index,
    setFieldValue,
    handleQuillChange,
    removeRow,
    addNewRow,
    dropdownRef,
    activeUnitIndex,
    setActiveUnitIndex,
    showRemove,
    dropdownOptions
}) => {
    const [searchTerm, setSearchTerm] = useState("");

    const filteredUnitOptions = useMemo(() =>
        dropdownOptions.filter(option =>
            option.toLowerCase().includes(searchTerm.toLowerCase())
        ),
        [searchTerm, dropdownOptions]
    );

    const handleBrandBlur = useCallback(() => {
        const inputString = material.brandsInput || "";
        setFieldValue(
            `materials.${index}.brands`,
            inputString.split(",").map(b => b.trim()).filter(Boolean)
        );
    }, [material.brandsInput, index, setFieldValue]);

    return (
        <div className="grid grid-cols-22 gap-x-1 mb-2">
            <p className="text-center">{index + 1}</p>

            <div className="col-span-1 self-start flex flex-col justify-end">
                <input
                    type="text"
                    value={material.code}
                    onChange={(e) => setFieldValue(`materials.${index}.code`, e.target.value)}
                    className="border border-[#DCDCD0] focus:outline-none focus:ring-0 p-1 h-[28px] w-full text-sm"
                />
                <ErrorMessage
                    name={`materials.${index}.code`}
                    component="div"
                    className="text-red-500 text-xs mt-1"
                />
            </div>

            <div className="col-span-4">
                <ReactQuill
                    value={material.name || ''}
                    onChange={(value) => {
                        // Immediate update without debounce for better UX
                        setFieldValue(`materials.${index}.name`, value);
                    }}
                    theme="snow"
                    className="min-h-[28px] max-h-[60px] p-0 word-break whitespace-normal focus:outline-none focus:ring-0 text-sm w-full overflow-y-auto remove-quill-padding"
                    modules={{ toolbar: false }}
                />
                <ErrorMessage
                    name={`materials.${index}.name`}
                    component="div"
                    className="text-red-500 text-xs mt-1"
                />
            </div>

            <div className="col-span-1 self-start flex flex-col justify-end">
                <input
                    type="text"
                    value={material.quantity}
                    // onChange={(e) => setFieldValue(`materials.${index}.quantity`, e.target.value)}
                    onChange={(e) => {
                        const inputValue = e.target.value;
                        // Allow only numbers with up to 2 decimals (no rounding)
                        if (/^\d{0,10}(\.\d{0,2})?$/.test(inputValue) || inputValue === "") {
                            setFieldValue(`materials.${index}.quantity`, inputValue);
                        }
                    }}
                    className="border border-[#DCDCD0] focus:outline-none focus:ring-0 p-1 h-[28px] w-full text-sm"
                />
                <ErrorMessage
                    name={`materials.${index}.quantity`}
                    component="div"
                    className="text-red-500 text-xs mt-1"
                />
            </div>
            <div className="col-span-2">
                <div className="relative self-start flex flex-col justify-end text-xs" ref={dropdownRef}>
                    <input
                        type="text"
                        value={material.unit}
                        onChange={(e) => {
                            setFieldValue(`materials.${index}.unit`, e.target.value);
                            setSearchTerm(e.target.value);
                        }}
                        onFocus={() => setActiveUnitIndex(index)}
                        onBlur={(e) => {
                            if (!dropdownRef.current.contains(e.relatedTarget)) {
                                if (!filteredUnitOptions.includes(material.unit)) {
                                    setFieldValue(`materials.${index}.unit`, "");
                                    setSearchTerm("");
                                }
                                setActiveUnitIndex(null);
                            }
                        }}
                        className="border border-[#DCDCD0] focus:outline-none focus:ring-0 p-2 h-[28px] w-full text-xs font-normal"
                    />

                    <img
                        src="https://storagereponeevaydevcdn.blob.core.windows.net/business/create_rfq_dropdown_arrow.svg"
                        alt="toggle"
                        className={`w-2 h-2 cursor-pointer bg-white absolute right-1 top-1/2 transform -translate-y-1/2 transition-transform duration-200 ${activeUnitIndex === index ? "rotate-180" : "rotate-0"
                            }`}
                    />

                    {activeUnitIndex === index && (
                        <ul className="absolute z-20 w-full font-normal bg-white border max-h-[100px] overflow-y-auto mt-1 shadow-lg" style={{ top: "100%", left: 0 }}>
                            {filteredUnitOptions.length > 0 ? (
                                filteredUnitOptions.map((option, i) => (
                                    <li
                                        key={i}
                                        tabIndex={-1}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            setFieldValue(`materials.${index}.unit`, option);
                                            setSearchTerm("");
                                            setActiveUnitIndex(null);
                                        }}
                                        className="p-2 cursor-pointer hover:bg-gray-100 text-xs"
                                    >
                                        {option}
                                    </li>
                                ))
                            ) : (
                                <li className="p-2 text-sm text-gray-500">No matching results</li>
                            )}
                        </ul>
                    )}

                </div>
                <ErrorMessage
                    name={`materials.${index}.unit`}
                    component="div"
                    className="text-red-500 text-xs mt-1"
                />
            </div>
            <div className="col-span-2 self-start flex flex-col justify-end">
                <input
                    type="text"
                    value={material?.budget ?? 0}
                    // onChange={(e) => setFieldValue(`materials.${index}.quantity`, e.target.value)}
                    onChange={(e) => {
                        const inputValue = e.target.value;

                        // Allow budget rates with up to 3 decimal places.
                        if (/^\d{0,10}(\.\d{0,3})?$/.test(inputValue) || inputValue === "") {
                            setFieldValue(`materials.${index}.budget`, inputValue);
                        }
                    }}
                    onBlur={() => {
                        if (material?.budget === "" || material?.budget === undefined || material?.budget === null) {
                            setFieldValue(`materials.${index}.budget`, 0);
                        }
                    }}
                    className="border border-[#DCDCD0] focus:outline-none focus:ring-0 p-1 h-[28px] w-full text-sm"
                />
                <ErrorMessage
                    name={`materials.${index}.budget`}
                    component="div"
                    className="text-red-500 text-xs mt-1"
                />
            </div>
            <div className="col-span-4 self-start flex flex-col justify-end">
                <input
                    type="text"
                    value={material.brandsInput ?? material.brands?.join(", ") ?? ''}
                    onChange={(e) => setFieldValue(`materials.${index}.brandsInput`, e.target.value)}
                    onBlur={handleBrandBlur}
                    className="border border-[#DCDCD0] p-2 h-[28px] w-full text-sm focus:outline-none focus:ring-0"
                />
            </div>

            <div className="col-span-6">
                <ReactQuill
                    value={material.specifications}
                    onChange={(value) => setFieldValue(`materials.${index}.specifications`, value)}
                    theme="snow"
                    className="min-h-[30px] max-h-[60px] p-0 word-break whitespace-normal focus:outline-none focus:ring-0 text-sm w-full overflow-y-auto remove-quill-padding"
                    modules={{ toolbar: false }}
                />
            </div>

            <div className="col-span-1 flex items-center justify-evenly">
                <button type="button" className="w-fit text-lg" onClick={addNewRow}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m7-7H5" />
                    </svg>
                </button>
                {showRemove && (
                    <button type="button" onClick={() => removeRow(index)} className="text-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
});

const UploadFileSection = React.memo(({ handleFileUploadMaterial, fileError, downloadMaterialTemplate, setOpenUploadFile }) => (
    <div className="flex flex-col justify-center items-center px-0">
        <div className="w-[400px] h-[180px] my-4 flex flex-col justify-center items-center border-2 border-dashed border-[#004491] bg-[#F1F7FF]">
            <input
                id="file-upload-material"
                type="file"
                className="hidden"
                onChange={handleFileUploadMaterial}
                accept=".xlsx, .xls"
            />
            <img src="https://storagereponeevaydevcdn.blob.core.windows.net/business/create_rfq_upload.svg" className='w-8 h-8 mb-2' />
            <p className="text-sm whitespace-nowrap">
                Drag & Drop or
                <label htmlFor="file-upload-material" className="underline px-2 text-[#1D28FA] cursor-pointer">
                    Choose File
                </label>
                to upload
            </p>
        </div>

        {fileError && <div className="text-red-500 text-sm">{fileError}</div>}

        <div>
            <p className='text-sm text-center w-full font-normal text-[#777777]'>Don't have the template? </p>
            <div className='flex items-center justify-center gap-2 pb-4 cursor-pointer'>
                <a onClick={downloadMaterialTemplate} download className='text-sm text-center text-[#1D28FA] font-normal'>
                    Download Here
                </a>
                <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M9.5625 1.5625C9.5625 0.974805 9.0877 0.5 8.5 0.5C7.9123 0.5 7.4375 0.974805 7.4375 1.5625V9.6209L5.00039 7.18379C4.58535 6.76875 3.91133 6.76875 3.49629 7.18379C3.08125 7.59883 3.08125 8.27285 3.49629 8.68789L7.74629 12.9379C8.16133 13.3529 8.83535 13.3529 9.25039 12.9379L13.5004 8.68789C13.9154 8.27285 13.9154 7.59883 13.5004 7.18379C13.0854 6.76875 12.4113 6.76875 11.9963 7.18379L9.5625 9.6209V1.5625ZM2.125 12.1875C0.95293 12.1875 0 13.1404 0 14.3125V15.375C0 16.5471 0.95293 17.5 2.125 17.5H14.875C16.0471 17.5 17 16.5471 17 15.375V14.3125C17 13.1404 16.0471 12.1875 14.875 12.1875H11.5049L10.0008 13.6916C9.1707 14.5217 7.82598 14.5217 6.9959 13.6916L5.49512 12.1875H2.125ZM14.3438 14.0469C14.5551 14.0469 14.7578 14.1308 14.9072 14.2803C14.0567 14.4297 15.1406 14.6324 15.1406 14.8438C15.1406 15.0551 15.0567 15.2578 14.9072 15.4072C14.7578 15.5567 14.5551 15.6406 14.3438 15.6406C14.1324 15.6406 13.9297 15.5567 13.7803 15.4072C13.6308 15.2578 13.5469 15.0551 13.5469 14.8438C13.5469 14.6324 13.6308 14.4297 13.7803 14.2803C13.9297 14.1308 14.1324 14.0469 14.3438 14.0469Z"
                        fill="blue"
                    />
                </svg>
            </div>
        </div>
    </div>
));

export default AddIndividual;
